import {
  convertHeadersToArray,
  getContentType,
  getContentTypeCategory,
} from '@carboncopyco/editor-utils';
import { CopyOutlineIcon } from '@carboncopyco/icons';
import { ColorHex } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { RulePreview } from '../../../RulePreview';
import type { Transaction } from '../../types';
import {
  type TransactionContent,
  TransactionContentFilter,
  getFilterOptions,
} from './TransactionContentFilter';
import { TransactionUrl } from './TransactionUrl';
import {
  ContentTypeBadge,
  MethodBadge,
  ModifiedBadge,
  StatusBadge,
} from './badges';

export interface TransactionPreviewProps {
  transaction: Transaction;
  open?: boolean;
  onTransactionContentChange?: (transactionContent: TransactionContent) => void;
  copyTransactionContent: () => void;
  searchValue?: string;
}

export const TransactionPreview = ({
  transaction,
  open,
  searchValue,
  onTransactionContentChange,
  copyTransactionContent,
}: TransactionPreviewProps) => {
  const { original, modified } = transaction;
  const filterOptions = getFilterOptions(original);
  const headers = convertHeadersToArray(transaction.original.response.headers);
  const contentType = getContentType(headers);
  const contentTypeCategory = getContentTypeCategory(contentType);
  const [transactionContent, setTransactionContent] =
    useState<TransactionContent>(
      filterOptions.find(({ disabled }) => !disabled)?.id ?? 'responseBody'
    );

  const changeTransactionContent = (transactionContent: TransactionContent) => {
    setTransactionContent(transactionContent);
    onTransactionContentChange?.(transactionContent);
  };

  useEffect(() => {
    onTransactionContentChange?.(transactionContent);
  }, []);

  return (
    <RulePreview>
      <div
        className={classNames(
          'w-full grid items-center',
          modified
            ? 'grid-cols-[75px_auto_75px_50px_65px]'
            : 'grid-cols-[75px_auto_50px_65px]'
        )}
      >
        <MethodBadge method={original.request.method} />
        <span className="flex items-center gap-4 justify-between overflow-hidden">
          <TransactionUrl
            url={original.request.url}
            searchValue={searchValue}
          />
          {open && (
            <span className="flex items-center gap-4">
              {contentTypeCategory !== 'image' && (
                <span
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyTransactionContent();
                  }}
                >
                  <CopyOutlineIcon
                    color={ColorHex.primary300}
                    size={20}
                  />
                </span>
              )}
              <span onClick={(e) => e.stopPropagation()}>
                <TransactionContentFilter
                  items={filterOptions}
                  selected={transactionContent}
                  onSelect={changeTransactionContent}
                />
              </span>
              <span className="bg-ds-natural-200 w-px h-7" />
            </span>
          )}
        </span>
        {modified && (
          <span className="justify-self-end">
            <ModifiedBadge />
          </span>
        )}
        <span className="justify-self-end">
          <StatusBadge status={original.response.status} />
        </span>
        <span className="pl-4">
          <ContentTypeBadge
            headers={original.response.headers}
            key={transactionContent}
          />
        </span>
      </div>
    </RulePreview>
  );
};
